import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { SvgIcons } from "@tripian/react";
import Model from "@tripian/model";
import {
  CITY_INFO,
  UPDATE_TRIP,
} from "../../../../constants/ROUTER_PATH_TITLE";
import TabMenu from "../../components/Itineraries/Tabs/Tabs";
import { useSelector } from "react-redux";
import ICombinedState from "../../../../redux/model/ICombinedState";
import { useIsMobileForTripLayout } from "../../../../hooks/useIsMobileForTripLayout";
import { useGuestUserForSimplyCaribbean } from "../../../../hooks/useGuestUserForSimplyCaribbean";

import classes from "./TripLayout.module.scss";

interface TripLayoutProps {
  tripReference: Model.TripReference;
  selectedTabIndex: number;
  isFocusOpen: boolean;
  setSelectedTabIndex: (index: number) => void;
  setShowCloneModal: () => void;
  setShowShareTripModal: () => void;
  onFocusLost: () => void;
  t: (value: Model.TranslationKey) => string;
  children: React.ReactNode;
  editPath?: string; // Custom edit path for timeline trips
}

const TripLayout: React.FC<TripLayoutProps> = ({
  tripReference,
  selectedTabIndex,
  isFocusOpen,
  setSelectedTabIndex,
  setShowCloneModal,
  setShowShareTripModal,
  onFocusLost,
  t,
  children,
  editPath,
}) => {
  const navigate = useNavigate();
  const sharedTrip = tripReference.shared;

  const { ref: tripLayoutRef, isMobile } = useIsMobileForTripLayout();

  const { readOnlyTrip } = useSelector((state: ICombinedState) => ({
    readOnlyTrip: state.trip.readOnly,
  }));

  const isGuestForSimplyCaribbean = useGuestUserForSimplyCaribbean();

  return (
    <div
      className={`${classes.tripLayout} container-height ${isFocusOpen ? classes.disabled : ""}`}
      ref={tripLayoutRef}
    >
      {isFocusOpen && (
        <div
          className={classes.tripLayoutOverlay}
          onClick={onFocusLost}
          aria-label="Close focus panel"
        />
      )}
      <div className="bg-background-color mt-10 w-full">
        {/* Header and tabs keep their own padding: KiwiHotelCard shrinks the padding of the px-8 that wraps it on mobile */}
        <div className="px-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <div className="flex items-start gap-2 mb-6">
                <span className="text-3xl/[1.875rem] font-medium max-w-[10rem] lg:max-w-full">
                  {tripReference.tripProfile.tripName || tripReference.city.name}
                </span>
                <div
                  className="cursor-pointer mt-1"
                  onClick={() =>
                    navigate(`${CITY_INFO.PATH}/${tripReference.city.id}`)
                  }
                  title="Click for more info"
                >
                  <SvgIcons.Info3 />
                </div>
              </div>
              {tripReference &&
                !(sharedTrip && window.tconfig.WIDGET_THEME_1) && (
                  <div className="text-base font-normal border border-neutral-300 rounded-full px-6 py-[0.4375rem] mt-2 min-w-52">
                    {moment(tripReference.tripProfile.arrivalDatetime).format(
                      "MMM DD"
                    )}{" "}
                    –{" "}
                    {moment(tripReference.tripProfile.departureDatetime).format(
                      "MMM DD YYYY"
                    )}
                  </div>
                )}
            </div>

            {!(sharedTrip && window.tconfig.WIDGET_THEME_1) && (
              <div className="flex gap-4">
                <div>
                  <div
                    className="border border-solid border-neutral-300 rounded-full w-10 h-10 flex justify-center items-center cursor-pointer"
                    onClick={() => {
                      if (isGuestForSimplyCaribbean) {
                        setShowCloneModal();
                      } else {
                        const path = editPath || `${UPDATE_TRIP.PATH}/${tripReference.tripHash}`;
                        navigate(path);
                      }
                    }}
                  >
                    <SvgIcons.Edit size="1.125rem" />
                  </div>
                  {window.tconfig.GUEST_LOGIN && (
                    <div className="text-sm text-center font-medium text-gray-600 max-w-[3.5rem] mt-1">
                      {t("trips.myTrips.localExperiences.tourDetails.edit")}
                    </div>
                  )}
                </div>

                {readOnlyTrip || isGuestForSimplyCaribbean ? (
                  <div>
                    <div
                      className="border border-solid border-neutral-300 rounded-full w-10 h-10 flex justify-center items-center cursor-pointer"
                      onClick={() => setShowCloneModal()}
                    >
                      <SvgIcons.Bookmark size="1.25rem" />
                    </div>
                    {window.tconfig.GUEST_LOGIN && (
                      <div className="text-sm text-center font-medium text-gray-600 max-w-[3.5rem] mt-1">
                        {t(
                          "trips.myTrips.itinerary.customPoiModal.locationModal.addLocation.address.submit"
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {window.tconfig.SHARED_TRIP && (
                      <>
                        {window.tconfig.GUEST_LOGIN ? (
                          <div>
                            <div
                              className="border border-solid border-neutral-300 rounded-full w-10 h-10 flex justify-center items-center cursor-pointer text-sm text-gray-600"
                              onClick={() => setShowShareTripModal()}
                            >
                              <SvgIcons.MobileArrowRight
                                width="1.25rem"
                                height="1.25rem"
                              />
                            </div>
                            <div className="text-sm font-medium text-gray-600 max-w-[3.5rem] mt-1">
                              {t("switchToMobile")}
                            </div>
                          </div>
                        ) : (
                          <div
                            className="border border-solid border-neutral-300 rounded-full w-10 h-10 flex justify-center items-center cursor-pointer"
                            onClick={() => setShowShareTripModal()}
                          >
                            <SvgIcons.Share
                              size="1.125rem"
                              className="primary-color"
                            />{" "}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <TabMenu
            menuItems={[
              {
                title: t("itinerary" as Model.TranslationKey),
                onClick: () => {
                  setSelectedTabIndex(0);
                },
              },
              {
                title: isMobile ? t("bookable.mobile") : t("bookable.desktop"),
                onClick: () => {
                  setSelectedTabIndex(1);
                },
              },
              {
                title: isMobile ? t("explore.mobile") : t("explore.desktop"),
                onClick: () => {
                  setSelectedTabIndex(2);
                },
              },

              ...(window.tconfig.BRAND_NAME === "Visa"
                ? [
                    {
                      title: isMobile ? t("visa.hotel.mobile" as Model.TranslationKey) : t("visa.hotel.desktop" as Model.TranslationKey),
                      onClick: () => setSelectedTabIndex(3),
                    },
                  ]
                : []),
            ]}
            selectedIndex={selectedTabIndex}
          />
        </div>
        <div className="px-8">{children}</div>
      </div>
    </div>
  );
};

export default TripLayout;
